import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { listItems } from '@/modules/inventory/services';
import type { InventoryItem } from '@/modules/inventory/types';
import {
  getMaintenanceTask,
  listMaintenanceTasksForItem,
  saveMaintenanceTask,
} from '@/modules/maintenance/services/maintenanceRepository';
import {
  MaintenanceStatusBadge,
  MaintenanceTypeBadge,
} from '@/modules/maintenance/components';
import {
  MAINTENANCE_STATUS_LABELS,
  MAINTENANCE_TYPE_LABELS,
  type MaintenanceStatus,
  type MaintenanceTaskInput,
  type MaintenanceType,
} from '@/modules/maintenance/types/maintenance';
import { listReservationsForItem } from '@/modules/reservations/services/reservationRepository';
import { isBlockingReservation } from '@/modules/reservations/utils/reservationFilters';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';
import type { Reservation } from '@/modules/reservations/types';

const defaultInput: MaintenanceTaskInput = {
  inventoryItemId: '',
  type: 'preventive',
  status: 'scheduled',
  title: '',
  description: '',
  scheduledStart: '',
  scheduledEnd: '',
  assignedTo: '',
  notes: '',
  preventiveIntervalDays: null,
  nextInspectionDate: null,
};

const TYPE_OPTIONS: MaintenanceType[] = [
  'preventive',
  'repair',
  'inspection',
  'cleaning',
];

const STATUS_OPTIONS: MaintenanceStatus[] = [
  'scheduled',
  'in_progress',
  'completed',
  'cancelled',
];

export function MaintenanceFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const { profile, hasPermission } = useAuth();
  const canWrite = hasPermission('maintenance.write');

  const [form, setForm] = useState<MaintenanceTaskInput>(defaultInput);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [history, setHistory] = useState<
    Awaited<ReturnType<typeof listMaintenanceTasksForItem>>
  >([]);
  const [affectedReservations, setAffectedReservations] = useState<Reservation[]>(
    [],
  );
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void listItems().then(setItems);
  }, []);

  useEffect(() => {
    if (isNew || !id) return;
    setLoading(true);
    void getMaintenanceTask(id)
      .then(async (task) => {
        if (!task) {
          setError('Tarea no encontrada.');
          return;
        }
        setForm({
          inventoryItemId: task.inventoryItemId,
          type: task.type,
          status: task.status,
          title: task.title,
          description: task.description,
          scheduledStart: task.scheduledStart,
          scheduledEnd: task.scheduledEnd,
          assignedTo: task.assignedTo,
          notes: task.notes,
          preventiveIntervalDays: task.preventiveIntervalDays,
          nextInspectionDate: task.nextInspectionDate,
        });
        const [hist, reservations] = await Promise.all([
          listMaintenanceTasksForItem(task.inventoryItemId),
          listReservationsForItem(
            task.inventoryItemId,
            task.scheduledStart,
            task.scheduledEnd,
          ),
        ]);
        setHistory(hist.filter((h) => h.id !== task.id));
        setAffectedReservations(
          reservations.filter((r) => isBlockingReservation(r)),
        );
      })
      .catch(() => setError('Error al cargar la tarea.'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const itemName = useMemo(
    () => items.find((i) => i.id === form.inventoryItemId)?.name ?? '—',
    [items, form.inventoryItemId],
  );

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canWrite || !profile) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await saveMaintenanceTask(
        form,
        profile.uid,
        isNew ? undefined : id,
      );
      void navigate(`${modulePaths.maintenance}/${saved.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="auth-muted">Cargando…</p>;

  return (
    <section>
      <p>
        <Link to={modulePaths.maintenance}>← Volver al listado</Link>
      </p>
      <h1>{isNew ? 'Nueva tarea' : 'Detalle de mantenimiento'}</h1>

      {!isNew && id ? (
        <div className="inventory-panel">
          <p>
            <strong>Artículo:</strong>{' '}
            <Link to={`${modulePaths.inventory}/${form.inventoryItemId}`}>
              {itemName}
            </Link>
          </p>
          <p>
            <MaintenanceTypeBadge type={form.type} />{' '}
            <MaintenanceStatusBadge status={form.status} />
          </p>
          {affectedReservations.length > 0 ? (
            <div className="auth-error" role="alert">
              <strong>Reservas que solapan:</strong>
              <ul>
                {affectedReservations.map((r) => (
                  <li key={r.id}>
                    <Link to={`${modulePaths.reservations}/${r.id}`}>
                      {r.title}
                    </Link>{' '}
                    ({r.startDate} → {r.endDate})
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {history.length > 0 ? (
            <>
              <h3>Historial del artículo</h3>
              <ul>
                {history.slice(0, 8).map((h) => (
                  <li key={h.id}>
                    <Link to={`${modulePaths.maintenance}/${h.id}`}>
                      {h.title}
                    </Link>{' '}
                    — {h.scheduledStart} ({MAINTENANCE_STATUS_LABELS[h.status]})
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}

      <form className="inventory-panel" onSubmit={handleSubmit}>
        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="users-form-grid">
          <label className="auth-label">
            Artículo *
            <select
              required
              disabled={!canWrite || !isNew}
              value={form.inventoryItemId}
              onChange={(e) =>
                setForm({ ...form, inventoryItemId: e.target.value })
              }
            >
              <option value="">Seleccionar…</option>
              {items.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.sku} — {i.name}
                </option>
              ))}
            </select>
          </label>
          <label className="auth-label">
            Tipo *
            <select
              disabled={!canWrite}
              value={form.type}
              onChange={(e) =>
                setForm({ ...form, type: e.target.value as MaintenanceType })
              }
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {MAINTENANCE_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
          <label className="auth-label">
            Estado *
            <select
              disabled={!canWrite}
              value={form.status}
              onChange={(e) =>
                setForm({
                  ...form,
                  status: e.target.value as MaintenanceStatus,
                })
              }
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {MAINTENANCE_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
          <label className="auth-label">
            Título *
            <input
              required
              disabled={!canWrite}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </label>
          <label className="auth-label">
            Inicio *
            <input
              type="date"
              required
              disabled={!canWrite}
              value={form.scheduledStart}
              onChange={(e) =>
                setForm({ ...form, scheduledStart: e.target.value })
              }
            />
          </label>
          <label className="auth-label">
            Fin *
            <input
              type="date"
              required
              disabled={!canWrite}
              value={form.scheduledEnd}
              onChange={(e) =>
                setForm({ ...form, scheduledEnd: e.target.value })
              }
            />
          </label>
          <label className="auth-label">
            Responsable
            <input
              disabled={!canWrite}
              value={form.assignedTo}
              onChange={(e) =>
                setForm({ ...form, assignedTo: e.target.value })
              }
            />
          </label>
          <label className="auth-label">
            Intervalo preventivo (días, placeholder)
            <input
              type="number"
              min={0}
              disabled={!canWrite}
              value={form.preventiveIntervalDays ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  preventiveIntervalDays: e.target.value
                    ? Number(e.target.value)
                    : null,
                })
              }
            />
          </label>
          <label className="auth-label">
            Próxima inspección (placeholder)
            <input
              type="date"
              disabled={!canWrite}
              value={form.nextInspectionDate ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  nextInspectionDate: e.target.value || null,
                })
              }
            />
          </label>
        </div>
        <label className="auth-label">
          Descripción
          <textarea
            disabled={!canWrite}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </label>
        <label className="auth-label">
          Notas
          <textarea
            disabled={!canWrite}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>
        {canWrite ? (
          <button type="submit" className="auth-button" disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        ) : (
          <p className="auth-muted">Solo lectura (sin permiso maintenance.write).</p>
        )}
      </form>
    </section>
  );
}
